import React, { useState, useEffect, useCallback } from "react";
import { useAuth, API } from "../App";
import axios from "axios";
import { toast } from "sonner";
import { Layout } from "../components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Avatar, AvatarFallback, AvatarImage } from "../components/ui/avatar";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Textarea } from "../components/ui/textarea";
import { 
  Users, 
  Star, 
  ShieldCheck, 
  ShieldOff,
  Trash2,
  Search,
  Eye,
  Ban,
  CheckCircle,
  XCircle,
  AlertTriangle,
  TrendingUp,
  Activity,
  Flag,
  ChevronLeft,
  ChevronRight,
  Loader2,
  Mail,
  Send,
  HardDrive,
  File,
  Image,
  FileVideo,
  Download
} from "lucide-react";

const AdminPage = () => {
  const { user, token } = useAuth();
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [usersPage, setUsersPage] = useState(1);
  const [usersTotalPages, setUsersTotalPages] = useState(1);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [selectedUser, setSelectedUser] = useState(null);
  const [userDetailsOpen, setUserDetailsOpen] = useState(false);
  const [banDialogOpen, setBanDialogOpen] = useState(false);
  const [banReason, setBanReason] = useState("");
  const [actionLoading, setActionLoading] = useState(false);
  const [sendingReport, setSendingReport] = useState(false);
  
  // Storage state
  const [storageStats, setStorageStats] = useState(null);
  const [storageFiles, setStorageFiles] = useState([]);
  const [storagePage, setStoragePage] = useState(1);
  const [storageTotalPages, setStorageTotalPages] = useState(1);
  const [storageFileType, setStorageFileType] = useState("all");
  const [storageLoading, setStorageLoading] = useState(false);

  // Check if user is admin
  const isAdmin = user?.email === "contact@rateshare.fr" || user?.is_admin;

  const fetchStats = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/admin/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(response.data);
    } catch (error) {
      console.error("Error fetching stats:", error);
    }
  }, [token]);

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/admin/users`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { page: usersPage, limit: 15, search: searchQuery, filter_status: filterStatus }
      });
      setUsers(response.data.users);
      setUsersTotalPages(response.data.pages);
    } catch (error) {
      console.error("Error fetching users:", error);
    } finally {
      setLoading(false);
    }
  }, [token, usersPage, searchQuery, filterStatus]);

  const fetchReports = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/admin/reports`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setReports(response.data.reports);
    } catch (error) {
      console.error("Error fetching reports:", error);
    }
  }, [token]);

  const fetchStorageStats = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/admin/storage/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStorageStats(response.data);
    } catch (error) {
      console.error("Error fetching storage stats:", error);
    }
  }, [token]);

  const fetchStorageFiles = useCallback(async () => {
    setStorageLoading(true);
    try {
      const response = await axios.get(`${API}/admin/storage/files`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { page: storagePage, limit: 15, file_type: storageFileType }
      });
      setStorageFiles(response.data.files);
      setStorageTotalPages(response.data.pages);
    } catch (error) {
      console.error("Error fetching storage files:", error);
    } finally {
      setStorageLoading(false);
    }
  }, [token, storagePage, storageFileType]);

  // Initial data fetch when admin
  useEffect(() => {
    if (isAdmin) {
      fetchStats();
      fetchUsers();
      fetchReports();
      fetchStorageStats();
      fetchStorageFiles();
    }
  }, [isAdmin, fetchStats, fetchUsers, fetchReports, fetchStorageStats, fetchStorageFiles]);

  const viewUserDetails = async (userId) => {
    try {
      const response = await axios.get(`${API}/admin/users/${userId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedUser(response.data);
      setUserDetailsOpen(true);
    } catch (error) {
      toast.error("Erreur lors du chargement des détails");
    }
  };

  const verifyUser = async (userId) => {
    setActionLoading(true);
    try {
      await axios.post(`${API}/admin/users/${userId}/verify`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Utilisateur vérifié");
      fetchUsers();
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur");
    } finally {
      setActionLoading(false);
    }
  };

  const banUser = async (userId) => {
    setActionLoading(true);
    try {
      await axios.post(`${API}/admin/users/${userId}/ban`, null, {
        headers: { Authorization: `Bearer ${token}` },
        params: { reason: banReason }
      });
      toast.success("Utilisateur banni");
      setBanDialogOpen(false);
      setBanReason("");
      fetchUsers();
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur");
    } finally {
      setActionLoading(false);
    }
  };

  const unbanUser = async (userId) => {
    setActionLoading(true);
    try {
      await axios.post(`${API}/admin/users/${userId}/unban`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Utilisateur débanni");
      fetchUsers();
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur");
    } finally {
      setActionLoading(false);
    }
  };

  const deleteUser = async (userId) => {
    if (!window.confirm("Êtes-vous sûr de vouloir supprimer cet utilisateur ? Cette action est irréversible.")) {
      return;
    }
    setActionLoading(true);
    try {
      await axios.delete(`${API}/admin/users/${userId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Utilisateur supprimé");
      setUserDetailsOpen(false);
      fetchUsers();
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur");
    } finally {
      setActionLoading(false);
    }
  };

  const resolveReport = async (reportId, action) => {
    try {
      await axios.post(`${API}/admin/reports/${reportId}/resolve`, null, {
        headers: { Authorization: `Bearer ${token}` },
        params: { action }
      });
      toast.success("Signalement résolu");
      fetchReports();
    } catch (error) {
      toast.error("Erreur");
    }
  };

  const sendDailyReport = async () => {
    setSendingReport(true);
    try {
      await axios.post(`${API}/admin/send-daily-report`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Rapport quotidien envoyé par email !");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de l'envoi");
    } finally {
      setSendingReport(false);
    }
  };

  const deleteStorageFile = async (fileId, fileType) => {
    if (!window.confirm("Êtes-vous sûr de vouloir supprimer ce fichier ?")) {
      return;
    }
    try {
      await axios.delete(`${API}/admin/storage/files/${fileId}?file_type=${fileType}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Fichier supprimé");
      fetchStorageFiles();
      fetchStorageStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de la suppression");
    }
  };

  const formatFileSize = (bytes) => {
    if (!bytes || bytes === 0) return "N/A";
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
  };

  const getFileIcon = (contentType) => {
    if (contentType?.startsWith("image/")) return <Image size={16} className="text-blue-500" />;
    if (contentType?.startsWith("video/")) return <FileVideo size={16} className="text-purple-500" />;
    return <File size={16} className="text-slate-500" />;
  };

  const getInitials = (name) => {
    return name?.split(" ").map(n => n[0]).join("").toUpperCase().slice(0, 2) || "?";
  };

  const getPhotoUrl = (path) => {
    if (path && token) {
      return `${process.env.REACT_APP_BACKEND_URL}/api/files/${path}?auth=${token}`;
    }
    return null;
  };

  if (!isAdmin) {
    return (
      <Layout>
        <div className="flex flex-col items-center justify-center py-20">
          <ShieldOff size={64} className="text-slate-300 mb-4" />
          <h1 className="text-2xl font-bold text-slate-900 mb-2">Accès refusé</h1>
          <p className="text-slate-500">Cette page est réservée aux administrateurs.</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-extrabold text-slate-900" style={{ fontFamily: 'Outfit' }}>
              Administration
            </h1>
            <p className="text-slate-500">Gérez les utilisateurs et surveillez l'activité</p>
          </div>
          <div className="flex items-center gap-2">
            <Button 
              onClick={sendDailyReport} 
              disabled={sendingReport}
              className="btn-guardian rounded-full"
              data-testid="send-report-btn"
            >
              {sendingReport ? (
                <Loader2 size={16} className="mr-2 animate-spin" />
              ) : (
                <Mail size={16} className="mr-2" />
              )}
              Envoyer rapport
            </Button>
            <Badge className="bg-[#FF3366] text-white">Admin</Badge>
          </div>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
            <Card className="guardian-card">
              <CardContent className="p-4 text-center">
                <Users size={24} className="mx-auto mb-2 text-blue-500" />
                <p className="text-2xl font-extrabold">{stats.totals.users}</p>
                <p className="text-xs text-slate-500">Utilisateurs</p>
              </CardContent>
            </Card>
            <Card className="guardian-card">
              <CardContent className="p-4 text-center">
                <Star size={24} className="mx-auto mb-2 text-amber-500" />
                <p className="text-2xl font-extrabold">{stats.totals.ratings}</p>
                <p className="text-xs text-slate-500">Évaluations</p>
              </CardContent>
            </Card>
            <Card className="guardian-card">
              <CardContent className="p-4 text-center">
                <ShieldCheck size={24} className="mx-auto mb-2 text-green-500" />
                <p className="text-2xl font-extrabold">{stats.totals.verified}</p>
                <p className="text-xs text-slate-500">Vérifiés</p>
              </CardContent>
            </Card>
            <Card className="guardian-card">
              <CardContent className="p-4 text-center">
                <Ban size={24} className="mx-auto mb-2 text-red-500" />
                <p className="text-2xl font-extrabold">{stats.totals.banned}</p>
                <p className="text-xs text-slate-500">Bannis</p>
              </CardContent>
            </Card>
            <Card className="guardian-card">
              <CardContent className="p-4 text-center">
                <Activity size={24} className="mx-auto mb-2 text-purple-500" />
                <p className="text-2xl font-extrabold">{stats.totals.visits}</p>
                <p className="text-xs text-slate-500">Visites</p>
              </CardContent>
            </Card>
            <Card className="guardian-card">
              <CardContent className="p-4 text-center">
                <TrendingUp size={24} className="mx-auto mb-2 text-emerald-500" />
                <p className="text-2xl font-extrabold">+{stats.today.users}</p>
                <p className="text-xs text-slate-500">Aujourd'hui</p>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Main Content */}
        <Tabs defaultValue="users">
          <TabsList className="rounded-full bg-slate-100 p-1">
            <TabsTrigger value="users" className="rounded-full data-[state=active]:bg-white font-bold">
              <Users size={16} className="mr-2" />
              Utilisateurs
            </TabsTrigger>
            <TabsTrigger value="reports" className="rounded-full data-[state=active]:bg-white font-bold">
              <Flag size={16} className="mr-2" />
              Signalements ({reports.filter(r => r.status === 'pending').length})
            </TabsTrigger>
            <TabsTrigger value="storage" className="rounded-full data-[state=active]:bg-white font-bold">
              <HardDrive size={16} className="mr-2" />
              Stockage
            </TabsTrigger>
          </TabsList>

          {/* Users Tab */}
          <TabsContent value="users" className="mt-6">
            <Card className="guardian-card">
              <CardHeader>
                <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
                  <CardTitle className="font-extrabold" style={{ fontFamily: 'Outfit' }}>
                    Tous les utilisateurs
                  </CardTitle>
                  <div className="flex gap-2 w-full sm:w-auto">
                    <div className="relative flex-1 sm:w-64">
                      <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                      <Input
                        placeholder="Rechercher..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="pl-9 rounded-full"
                      />
                    </div>
                    <select
                      value={filterStatus}
                      onChange={(e) => setFilterStatus(e.target.value)}
                      className="px-3 py-2 border rounded-full text-sm"
                    >
                      <option value="all">Tous</option>
                      <option value="verified">Vérifiés</option>
                      <option value="unverified">Non vérifiés</option>
                      <option value="banned">Bannis</option>
                    </select>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="flex justify-center py-8">
                    <Loader2 size={32} className="animate-spin text-slate-400" />
                  </div>
                ) : (
                  <>
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead>
                          <tr className="border-b">
                            <th className="text-left py-3 px-2 text-sm font-bold text-slate-500">Utilisateur</th>
                            <th className="text-left py-3 px-2 text-sm font-bold text-slate-500">Email</th>
                            <th className="text-center py-3 px-2 text-sm font-bold text-slate-500">Note</th>
                            <th className="text-center py-3 px-2 text-sm font-bold text-slate-500">Statut</th>
                            <th className="text-right py-3 px-2 text-sm font-bold text-slate-500">Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {users.map((u) => (
                            <tr key={u.id} className="border-b hover:bg-slate-50">
                              <td className="py-3 px-2">
                                <div className="flex items-center gap-3">
                                  <Avatar className="h-10 w-10">
                                    <AvatarImage src={getPhotoUrl(u.profile_photo)} />
                                    <AvatarFallback className="bg-[#FF3366] text-white text-sm">
                                      {getInitials(u.full_name)}
                                    </AvatarFallback>
                                  </Avatar>
                                  <div>
                                    <p className="font-bold text-sm">{u.full_name}</p>
                                    <p className="text-xs text-slate-500">{u.level}</p>
                                  </div>
                                </div>
                              </td>
                              <td className="py-3 px-2 text-sm text-slate-600">{u.email}</td>
                              <td className="py-3 px-2 text-center">
                                <span className="font-bold">{u.average_rating?.toFixed(1) || "0.0"}</span>
                                <span className="text-xs text-slate-400 ml-1">({u.total_ratings})</span>
                              </td>
                              <td className="py-3 px-2 text-center">
                                {u.is_banned ? (
                                  <Badge variant="destructive" className="text-xs">Banni</Badge>
                                ) : u.is_verified ? (
                                  <Badge className="bg-green-100 text-green-700 text-xs">Vérifié</Badge>
                                ) : (
                                  <Badge variant="outline" className="text-xs">Non vérifié</Badge>
                                )}
                              </td>
                              <td className="py-3 px-2 text-right">
                                <div className="flex justify-end gap-1">
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => viewUserDetails(u.id)}
                                    className="h-8 w-8 p-0"
                                  >
                                    <Eye size={16} />
                                  </Button>
                                  {!u.is_verified && (
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => verifyUser(u.id)}
                                      className="h-8 w-8 p-0 text-green-600"
                                    >
                                      <CheckCircle size={16} />
                                    </Button>
                                  )}
                                  {!u.is_banned ? (
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => {
                                        setSelectedUser({ user: u });
                                        setBanDialogOpen(true);
                                      }}
                                      className="h-8 w-8 p-0 text-red-600"
                                    >
                                      <Ban size={16} />
                                    </Button>
                                  ) : (
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => unbanUser(u.id)}
                                      className="h-8 w-8 p-0 text-blue-600"
                                    >
                                      <ShieldCheck size={16} />
                                    </Button>
                                  )}
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    {/* Pagination */}
                    <div className="flex items-center justify-between mt-4">
                      <p className="text-sm text-slate-500">
                        Page {usersPage} sur {usersTotalPages}
                      </p>
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setUsersPage(p => Math.max(1, p - 1))}
                          disabled={usersPage === 1}
                          className="rounded-full"
                        >
                          <ChevronLeft size={16} />
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setUsersPage(p => Math.min(usersTotalPages, p + 1))}
                          disabled={usersPage === usersTotalPages}
                          className="rounded-full"
                        >
                          <ChevronRight size={16} />
                        </Button>
                      </div>
                    </div>
                  </>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Reports Tab */}
          <TabsContent value="reports" className="mt-6">
            <Card className="guardian-card">
              <CardHeader>
                <CardTitle className="font-extrabold" style={{ fontFamily: 'Outfit' }}>
                  Signalements
                </CardTitle>
              </CardHeader>
              <CardContent>
                {reports.length === 0 ? (
                  <div className="text-center py-8 text-slate-500">
                    <Flag size={48} className="mx-auto mb-4 opacity-30" />
                    <p>Aucun signalement</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {reports.map((report) => (
                      <div key={report.id} className={`p-4 rounded-xl border ${report.status === 'pending' ? 'bg-red-50 border-red-200' : 'bg-slate-50'}`}>
                        <div className="flex items-start justify-between">
                          <div>
                            <p className="font-bold text-sm">
                              {report.reporter_name} a signalé un utilisateur
                            </p>
                            <p className="text-sm text-slate-600 mt-1">
                              <strong>Raison:</strong> {report.reason}
                            </p>
                            {report.details && (
                              <p className="text-sm text-slate-500 mt-1">{report.details}</p>
                            )}
                            <p className="text-xs text-slate-400 mt-2">
                              {new Date(report.created_at).toLocaleString('fr-FR')}
                            </p>
                          </div>
                          {report.status === 'pending' && (
                            <div className="flex gap-2">
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => resolveReport(report.id, 'dismissed')}
                                className="rounded-full"
                              >
                                <XCircle size={14} className="mr-1" />
                                Ignorer
                              </Button>
                              <Button
                                size="sm"
                                onClick={() => {
                                  viewUserDetails(report.reported_user_id);
                                }}
                                className="btn-guardian rounded-full"
                              >
                                <Eye size={14} className="mr-1" />
                                Voir
                              </Button>
                            </div>
                          )}
                          {report.status === 'resolved' && (
                            <Badge variant="outline" className="text-green-600">Résolu</Badge>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Storage Tab */}
          <TabsContent value="storage" className="mt-6 space-y-6">
            {/* Storage Stats Cards */}
            {storageStats && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Card className="guardian-card">
                  <CardContent className="p-4 text-center">
                    <HardDrive size={24} className="mx-auto mb-2 text-blue-500" />
                    <p className="text-2xl font-extrabold">{storageStats.totals.files}</p>
                    <p className="text-xs text-slate-500">Total fichiers</p>
                  </CardContent>
                </Card>
                <Card className="guardian-card">
                  <CardContent className="p-4 text-center">
                    <File size={24} className="mx-auto mb-2 text-amber-500" />
                    <p className="text-2xl font-extrabold">{storageStats.totals.proofs}</p>
                    <p className="text-xs text-slate-500">Preuves</p>
                  </CardContent>
                </Card>
                <Card className="guardian-card">
                  <CardContent className="p-4 text-center">
                    <Image size={24} className="mx-auto mb-2 text-green-500" />
                    <p className="text-2xl font-extrabold">{storageStats.totals.profile_photos}</p>
                    <p className="text-xs text-slate-500">Photos profil</p>
                  </CardContent>
                </Card>
                <Card className="guardian-card">
                  <CardContent className="p-4 text-center">
                    <Download size={24} className="mx-auto mb-2 text-purple-500" />
                    <p className="text-2xl font-extrabold">{storageStats.totals.size_mb} MB</p>
                    <p className="text-xs text-slate-500">Espace utilisé</p>
                  </CardContent>
                </Card>
              </div>
            )}

            {/* Files List */}
            <Card className="guardian-card">
              <CardHeader>
                <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
                  <CardTitle className="font-extrabold" style={{ fontFamily: 'Outfit' }}>
                    Fichiers uploadés
                  </CardTitle>
                  <select
                    value={storageFileType}
                    onChange={(e) => {
                      setStorageFileType(e.target.value);
                      setStoragePage(1);
                    }}
                    className="px-3 py-2 border rounded-full text-sm"
                  >
                    <option value="all">Tous les fichiers</option>
                    <option value="proofs">Preuves uniquement</option>
                    <option value="profiles">Photos de profil</option>
                  </select>
                </div>
              </CardHeader>
              <CardContent>
                {storageLoading ? (
                  <div className="flex justify-center py-8">
                    <Loader2 size={32} className="animate-spin text-slate-400" />
                  </div>
                ) : storageFiles.length === 0 ? (
                  <div className="text-center py-8 text-slate-500">
                    <HardDrive size={48} className="mx-auto mb-4 opacity-30" />
                    <p>Aucun fichier</p>
                  </div>
                ) : (
                  <>
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead>
                          <tr className="border-b">
                            <th className="text-left py-3 px-2 text-sm font-bold text-slate-500">Type</th>
                            <th className="text-left py-3 px-2 text-sm font-bold text-slate-500">Fichier</th>
                            <th className="text-left py-3 px-2 text-sm font-bold text-slate-500">Utilisateur</th>
                            <th className="text-center py-3 px-2 text-sm font-bold text-slate-500">Taille</th>
                            <th className="text-center py-3 px-2 text-sm font-bold text-slate-500">Date</th>
                            <th className="text-right py-3 px-2 text-sm font-bold text-slate-500">Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {storageFiles.map((file) => (
                            <tr key={`${file.type}-${file.id}`} className="border-b hover:bg-slate-50">
                              <td className="py-3 px-2">
                                <div className="flex items-center gap-2">
                                  {getFileIcon(file.content_type)}
                                  <Badge variant="outline" className="text-xs">
                                    {file.type === "proof" ? "Preuve" : "Photo"}
                                  </Badge>
                                </div>
                              </td>
                              <td className="py-3 px-2">
                                <p className="font-medium text-sm truncate max-w-[150px]" title={file.filename}>
                                  {file.filename}
                                </p>
                                <p className="text-xs text-slate-400">{file.content_type}</p>
                              </td>
                              <td className="py-3 px-2 text-sm">
                                {file.type === "proof" ? (
                                  <div>
                                    <p className="font-medium">{file.rater_name}</p>
                                    <p className="text-xs text-slate-400">→ {file.rated_user_name}</p>
                                  </div>
                                ) : (
                                  <div>
                                    <p className="font-medium">{file.user_name}</p>
                                    <p className="text-xs text-slate-400">{file.user_email}</p>
                                  </div>
                                )}
                              </td>
                              <td className="py-3 px-2 text-center text-sm">
                                {formatFileSize(file.size)}
                              </td>
                              <td className="py-3 px-2 text-center text-xs text-slate-500">
                                {file.created_at ? new Date(file.created_at).toLocaleDateString('fr-FR') : "N/A"}
                              </td>
                              <td className="py-3 px-2 text-right">
                                <div className="flex justify-end gap-1">
                                  {file.storage_path && (
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => window.open(`${process.env.REACT_APP_BACKEND_URL}/api/files/${file.storage_path}?auth=${token}`, '_blank')}
                                      className="h-8 w-8 p-0"
                                      title="Voir"
                                    >
                                      <Eye size={16} />
                                    </Button>
                                  )}
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => deleteStorageFile(file.id, file.type === "proof" ? "proof" : "profile_photo")}
                                    className="h-8 w-8 p-0 text-red-600"
                                    title="Supprimer"
                                  >
                                    <Trash2 size={16} />
                                  </Button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    {/* Pagination */}
                    <div className="flex items-center justify-between mt-4">
                      <p className="text-sm text-slate-500">
                        Page {storagePage} sur {storageTotalPages}
                      </p>
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setStoragePage(p => Math.max(1, p - 1))}
                          disabled={storagePage === 1}
                          className="rounded-full"
                        >
                          <ChevronLeft size={16} />
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setStoragePage(p => Math.min(storageTotalPages, p + 1))}
                          disabled={storagePage === storageTotalPages}
                          className="rounded-full"
                        >
                          <ChevronRight size={16} />
                        </Button>
                      </div>
                    </div>
                  </>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* User Details Dialog */}
        <Dialog open={userDetailsOpen} onOpenChange={setUserDetailsOpen}>
          <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto rounded-3xl">
            <DialogHeader>
              <DialogTitle className="font-extrabold" style={{ fontFamily: 'Outfit' }}>
                Détails de l'utilisateur
              </DialogTitle>
            </DialogHeader>
            {selectedUser && (
              <div className="space-y-6">
                {/* User Info */}
                <div className="flex items-center gap-4">
                  <Avatar className="h-20 w-20">
                    <AvatarImage src={getPhotoUrl(selectedUser.user.profile_photo)} />
                    <AvatarFallback className="bg-[#FF3366] text-white text-2xl">
                      {getInitials(selectedUser.user.full_name)}
                    </AvatarFallback>
                  </Avatar>
                  <div>
                    <h3 className="text-xl font-bold">{selectedUser.user.full_name}</h3>
                    <p className="text-slate-500">{selectedUser.user.email}</p>
                    <div className="flex gap-2 mt-2">
                      {selectedUser.user.is_verified && (
                        <Badge className="bg-green-100 text-green-700">Vérifié</Badge>
                      )}
                      {selectedUser.user.is_banned && (
                        <Badge variant="destructive">Banni</Badge>
                      )}
                      <Badge variant="outline">{selectedUser.user.level}</Badge>
                    </div>
                  </div>
                </div>

                {/* Stats */}
                <div className="grid grid-cols-3 gap-4">
                  <div className="text-center p-3 bg-slate-50 rounded-xl">
                    <p className="text-2xl font-bold">{selectedUser.user.average_rating?.toFixed(1) || "0.0"}</p>
                    <p className="text-xs text-slate-500">Note moyenne</p>
                  </div>
                  <div className="text-center p-3 bg-slate-50 rounded-xl">
                    <p className="text-2xl font-bold">{selectedUser.received_ratings?.length || 0}</p>
                    <p className="text-xs text-slate-500">Avis reçus</p>
                  </div>
                  <div className="text-center p-3 bg-slate-50 rounded-xl">
                    <p className="text-2xl font-bold">{selectedUser.given_ratings?.length || 0}</p>
                    <p className="text-xs text-slate-500">Avis donnés</p>
                  </div>
                </div>

                {/* Reports */}
                {selectedUser.reports && selectedUser.reports.length > 0 && (
                  <div className="p-4 bg-red-50 rounded-xl border border-red-200">
                    <h4 className="font-bold text-red-700 mb-2">
                      <AlertTriangle size={16} className="inline mr-1" />
                      {selectedUser.reports.length} signalement(s)
                    </h4>
                    {selectedUser.reports.map((r) => (
                      <p key={r.id || r.reason} className="text-sm text-red-600">• {r.reason}</p>
                    ))}
                  </div>
                )}

                {/* Actions */}
                <DialogFooter className="flex gap-2">
                  {!selectedUser.user.is_verified && (
                    <Button
                      onClick={() => {
                        verifyUser(selectedUser.user.id);
                        setUserDetailsOpen(false);
                      }}
                      className="bg-green-500 hover:bg-green-600 rounded-full"
                    >
                      <CheckCircle size={16} className="mr-2" />
                      Vérifier
                    </Button>
                  )}
                  {!selectedUser.user.is_banned ? (
                    <Button
                      variant="destructive"
                      onClick={() => {
                        setUserDetailsOpen(false);
                        setBanDialogOpen(true);
                      }}
                      className="rounded-full"
                    >
                      <Ban size={16} className="mr-2" />
                      Bannir
                    </Button>
                  ) : (
                    <Button
                      onClick={() => {
                        unbanUser(selectedUser.user.id);
                        setUserDetailsOpen(false);
                      }}
                      className="rounded-full"
                    >
                      <ShieldCheck size={16} className="mr-2" />
                      Débannir
                    </Button>
                  )}
                  <Button
                    variant="outline"
                    onClick={() => deleteUser(selectedUser.user.id)}
                    className="rounded-full text-red-600 hover:bg-red-50"
                  >
                    <Trash2 size={16} className="mr-2" />
                    Supprimer
                  </Button>
                </DialogFooter>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Ban Dialog */}
        <Dialog open={banDialogOpen} onOpenChange={setBanDialogOpen}>
          <DialogContent className="rounded-3xl">
            <DialogHeader>
              <DialogTitle className="font-extrabold" style={{ fontFamily: 'Outfit' }}>
                Bannir l'utilisateur
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <p className="text-slate-600">
                Êtes-vous sûr de vouloir bannir <strong>{selectedUser?.user?.full_name}</strong> ?
              </p>
              <div>
                <label className="text-sm font-bold">Raison du bannissement</label>
                <Textarea
                  value={banReason}
                  onChange={(e) => setBanReason(e.target.value)}
                  placeholder="Décrivez la raison..."
                  className="mt-2 rounded-xl"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setBanDialogOpen(false)} className="rounded-full">
                Annuler
              </Button>
              <Button
                variant="destructive"
                onClick={() => banUser(selectedUser?.user?.id)}
                disabled={actionLoading}
                className="rounded-full"
              >
                {actionLoading ? <Loader2 size={16} className="animate-spin mr-2" /> : <Ban size={16} className="mr-2" />}
                Confirmer le bannissement
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default AdminPage;
